import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Checkbox,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import { searchStyles } from "../../styles/formStyles";
import DeliveryDetailsView from "./DeliveryDetailsView";
import ReassignDeliveryPartner from "./ReassignDeliveryPartner";
import DeliveryFilterDialog from "./DeliveryFilterDialog";
import BulkAssignDialog from "./BulkAssignDialog";
import { getOrderStatusStyle, formatOrderStatus, formatDisplayDate } from "../../util/Util";
import { IDeliveryListDTO, IOrderStatsDTO } from "../../interfaces/Delivery";
import { getDeliveryList, getDeliveryStats } from "../../services/Delivery";

const overviewCards = [
  { title: "Packages in System", status: "all" },
  { title: "Packages Delivered", status: "Delivered" },
  { title: "Yet to be Assigned", status: "Packed" },
  { title: "Assigned", status: "Assigned" },
  { title: "Out for Delivery", status: "Out_for_delivery" },
  { title: "Packages Delayed", status: "Delayed" },
];

const DeliveryOverview =({ selectedStatus: initialStatus, onStatusChange, directPackageId, onDirectPackageIdConsumed }: { selectedStatus?: string | null; onStatusChange?: () => void; directPackageId?: string | null; onDirectPackageIdConsumed?: () => void }) => {
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(initialStatus || "all");
  const [page, setPage] = useState(1);
  const [cardStats,setCardStats] = useState<IOrderStatsDTO | null>(null);
  const [delivery, setDelivery] = useState<Array<IDeliveryListDTO>>([]);
  const [loading, setLoading] = useState(false);
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<IDeliveryListDTO | null >(null);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignDelivery, setReassignDelivery] = useState<IDeliveryListDTO |null>(null);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<{ pincodes: string[]; deliveryFrom: string; deliveryTo: string }>({ pincodes: [], deliveryFrom: "", deliveryTo: "" });
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState<IDeliveryListDTO[]>([]);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);

  const rowsPerPage = 5;

  React.useEffect(() => {
    if (initialStatus) {
      setSelectedStatus(initialStatus);
      if (onStatusChange) onStatusChange();
    }
  }, [initialStatus, onStatusChange]);

  React.useEffect(() => {
    if (directPackageId) {
      setSelectedDelivery({ packageId: directPackageId } as IDeliveryListDTO);
      setShowDetailsView(true);
      if (onDirectPackageIdConsumed) onDirectPackageIdConsumed();
    }
  }, [directPackageId]);
  const fetchAllDeliveries = async (status: string, searchKeyword?: string, pincodes?: string[], fromDate?: string, toDate?: string) => {
    try {
      setLoading(true);
      const deliveryRes = await getDeliveryList(status, searchKeyword, pincodes, fromDate, toDate);
      setDelivery(Array.isArray(deliveryRes) ? deliveryRes : []);
    } catch {
      setDelivery([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await getDeliveryStats();
      setCardStats(res ?? null);
    } catch {
      setCardStats(null);
    }
  };

  const fetchWithFilters = (status?: string) => {
    const s = status ?? selectedStatus;
    fetchAllDeliveries(s, search || undefined, filters.pincodes.length ? filters.pincodes : undefined, filters.deliveryFrom || undefined, filters.deliveryTo || undefined);
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchAllDeliveries(selectedStatus, value || undefined, filters.pincodes.length ? filters.pincodes : undefined, filters.deliveryFrom || undefined, filters.deliveryTo || undefined);
    }, 500);
  };

  useEffect(() => {
    fetchAllDeliveries("all");
    fetchStats();
  }, []);

  const statsMap: Record<string, keyof IOrderStatsDTO> = {
    all: "totalOrders",
    Delivered: "delivered",
    Packed: "packed",
    Assigned: "assigned",
    Out_for_delivery: "outForDelivery",
    Delayed: "delayed",
  };

  const getCardCount = (status: string) => {
    if (!cardStats) return 0;
    return cardStats[statsMap[status]] ?? 0;
  };

  const handleCardClick = (status: string) => {
    setSelectedStatus(status);
    setPage(1);
    fetchAllDeliveries(status, search || undefined, filters.pincodes.length ? filters.pincodes : undefined, filters.deliveryFrom || undefined, filters.deliveryTo || undefined);
  };



  const handleItemSelect = (itemId: string, row: IDeliveryListDTO) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
      setSelectedDeliveries(selectedDeliveries.filter(d => String(d.orderId) !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
      setSelectedDeliveries([...selectedDeliveries, row]);
    }
  };

  const handleViewClick = (delivery: any) => {
    setSelectedDelivery(delivery);
    setShowDetailsView(true);
  };

  const handleBackToList = () => {
    setShowDetailsView(false);
    setSelectedDelivery(null);
  };

  const handleReassignClick = (delivery: any) => {
    setReassignDelivery(delivery);
    setShowReassignDialog(true);
  };

  const filteredData = delivery;
  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  if (showDetailsView) {
    return <DeliveryDetailsView deliveryData={selectedDelivery} onBack={handleBackToList} />;
  }

  return (
    <>
      <Grid container spacing={2} sx={{ mb:1}}>
        {overviewCards.map((card, index) => (
          <Grid item xs={2} key={index}>
            <Card
              onClick={() => handleCardClick(card.status)}
              sx={{
                backgroundColor: selectedStatus === card.status ? "primary.light" : "background.paper",
                height: 100,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { backgroundColor: selectedStatus === card.status ? "primary.light" : "background.paper" },
                border: selectedStatus === card.status ? "2px solid" : "1px solid",
                borderColor: selectedStatus === card.status ? "primary.main" : "grey.300",
              }}
            >
              <CardContent sx={{ textAlign: "center", p: 2, width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "grey.primary", display: "block", mb: 1, wordWrap: "break-word", lineHeight: 1.2, textAlign: "center" }}>
                  {card.title}
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ color: selectedStatus === card.status ? "primary.main" : "text.primary", fontSize: "2rem", lineHeight: 1 }}>
                  {getCardCount(card.status)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p:1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <TextField
            size="small"
            placeholder="Search by delivery partner, customer name, package..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={searchStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {selectedItems.length > 0 && selectedStatus === "Packed" && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setShowBulkAssignDialog(true)}
                variant="contained"
                sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "primary.main", "&:hover": { bgcolor: "primary.main" } }}
              >
                Assign
              </Button>
            )}
            <Button
              startIcon={<FilterAltOutlinedIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ borderRadius: "6px", border: "1px solid rgba(74, 85, 101, 0.10)", backgroundColor: "#FFF", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", textTransform: "none", color: "text.primary", px: 2, "&:hover": { backgroundColor: "#FFF" } }}
            >
              Filter
            </Button>
          </Box>
        </Box>

        <Box sx={{ p:1, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Paper sx={{ maxHeight: "500px", overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {selectedStatus === "Packed" && (
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, width: 50, whiteSpace: "nowrap" }}>
                      <Checkbox
                        checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                        indeterminate={selectedItems.length > 0 && selectedItems.length < paginatedData.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(paginatedData.map(item => String(item.orderId)));
                            setSelectedDeliveries([...paginatedData]);
                          } else {
                            setSelectedItems([]);
                            setSelectedDeliveries([]);
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Delivery ID</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Customer Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Address</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Pincode</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Package</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Delivery partner</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Delivery Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={selectedStatus === "Packed" ? 10 : 9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={selectedStatus === "Packed" ? 10 : 9} align="center" sx={{ py: 2 }}>
                      <Typography color="grey.500">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow key={row.orderId} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      {selectedStatus === "Packed" && (
                        <TableCell align="center" sx={{ p: 1 }}>
                          <Checkbox
                            checked={selectedItems.includes(String(row.orderId))}
                            onChange={() => handleItemSelect(String(row.orderId), row)}
                          />
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ p: 1 }}>{row.orderId}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{row.customerName}</TableCell>
                      <TableCell align="center" sx={{ p: 1, maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <Tooltip title={row.address} arrow>
                          <span>{row.address}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{row.pincode}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{row.packageId}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{row.deliveryPartnerName || "--------"}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{formatDisplayDate(row.deliveryDate)}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box sx={{ display: "inline-block", px: "0.7rem", py: "0.25rem", borderRadius: "6px", fontSize: "12px", fontWeight: 600, lineHeight: "16px", textAlign: "center", backgroundColor: getOrderStatusStyle(row.status).bg, color: getOrderStatusStyle(row.status).color, border: getOrderStatusStyle(row.status).border, minWidth: "120px" }}>
                          {formatOrderStatus(row.status)}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: "0.5rem" }}>
                          <Button startIcon={<VisibilityOutlinedIcon />} onClick={() => handleViewClick(row)} sx={{ color: "grey.700", textTransform: "none", fontSize: "0.8rem" }}>
                            View
                          </Button>
                          {(row.status === "Delayed") && (
                            <Button startIcon={<PersonOutlineOutlinedIcon />} onClick={() => handleReassignClick(row)} sx={{ color: "primary.main", textTransform: "none", fontSize: "0.8rem" }}>
                              Reassign
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {filteredData.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center"}}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}

      <ReassignDeliveryPartner open={showReassignDialog} onClose={() => setShowReassignDialog(false)} onSave={() => { fetchWithFilters(); fetchStats(); }} deliveryItem={reassignDelivery} />
      <DeliveryFilterDialog
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        selectedStatus={selectedStatus}
        search={search}
        onApply={(newFilters, data) => {
          setFilters(newFilters);
          setFilterAnchor(null);
          setPage(1);
          setDelivery(data);
        }}
      />
      <BulkAssignDialog 
        open={showBulkAssignDialog} 
        onClose={() => setShowBulkAssignDialog(false)} 
        onSave={() => { fetchWithFilters(); fetchStats(); setSelectedItems([]); setSelectedDeliveries([]); }}
        selectedDeliveries={selectedDeliveries}
      />
    </>
  );
};
export default DeliveryOverview;
